/**
 * Redux slice for Inspection Details (GraphQL version)
 * Uses GraphQL API via services-px instead of REST API
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { resetALL } from 'Redux/actions';
import { fetchInspectionDetails } from '../../../../../services-px/inspectionApi';
import { fetchDefectPhotos } from '../../../../../services-px/defectsApi';

const initialState = {
	inspection: null,
	staticInfo: null,
	assetData: [],
	inspectionPhotos: [],
	loading: false,
	error: null,
};

// Helper to get translated label from translatable string object
const getTranslatedLabel = (translatableObj) => {
	if (!translatableObj) return null;
	if (typeof translatableObj === 'string') return translatableObj;
	if (typeof translatableObj === 'object' && translatableObj.languageLabel) {
		return translatableObj.languageLabel;
	}
	if (typeof translatableObj === 'object') {
		return translatableObj.en || translatableObj['en-us'] || Object.values(translatableObj)[0];
	}
	return null;
};

// Map severity number to label and icon
const getSeverityInfo = (severity) => {
	const severityMap = {
		255: { label: 'Safety Critical', color: '#d32f2f', icon: '▲' },
		192: { label: 'Pre-Trip Critical', color: '#f57c00', icon: '▲' },
		128: { label: 'Safety Advisory', color: '#fbc02d', icon: '▲' },
		127: { label: 'Safety Advisory', color: '#fbc02d', icon: '▲' },
		64: { label: 'Maintenance Required', color: '#1976d2', icon: '▲' },
	};
	return severityMap[severity] || { label: 'Unknown', color: '#757575', icon: '▲' };
};

// Format inspector name
const formatInspectorName = (firstName, lastName) => {
	if (!firstName && !lastName) return null;
	if (!firstName) return lastName;
	if (!lastName) return firstName;
	return `${firstName} ${lastName}`;
};

// Count all defect photos from inspection assets
// Traverses: inspectionAssets[] -> inspectionZones[] -> inspectionComponents[] -> inspectionDefects[] -> defectMedia[]
const countAllDefectPhotos = (inspectionAssets) => {
	if (!inspectionAssets || !Array.isArray(inspectionAssets)) return 0;

	let totalDefectPhotos = 0;

	inspectionAssets.forEach((asset) => {
		const zones = asset.inspectionZones || [];
		zones.forEach((zone) => {
			const components = zone.inspectionComponents || [];
			components.forEach((component) => {
				const defects = component.inspectionDefects || [];
				defects.forEach((defect) => {
					const mediaCount = defect.defectMedia?.length || 0;
					totalDefectPhotos += mediaCount;
				});
			});
		});
	});

	return totalDefectPhotos;
};

// Parse driver name from "LastName, FirstName" format
const parseDriverName = (driverName) => {
	if (!driverName) return { firstName: null, lastName: null };
	const parts = driverName.split(',').map(s => s.trim());
	return {
		lastName: parts[0] || null,
		firstName: parts[1] || null,
	};
};

// Imperial units for finding correct index in conversion pairs
const imperialUnits = ['mi', 'gal', 'mph', 'mpg', 'psi', 'F', 'oz', 'lb', 'ton', 'ft', 'in', 'yd', 'hrs'];

// Helper to find imperial unit index in conversion pair
const findImperialIndex = (unitPair) => {
	if (!unitPair || !Array.isArray(unitPair) || unitPair.length <= 1) return 0;
	const idx = unitPair.findIndex((unit) => imperialUnits.includes(unit));
	return idx >= 0 ? idx : 0;
};

// Format array value with proper unit selection
const formatArrayValue = (content, unitConversionPair) => {
	const unitIndex = findImperialIndex(unitConversionPair);
	const rawValue = content[unitIndex] ?? content[0];

	// Skip if value is empty, 0, null, or undefined
	if (rawValue === '' || rawValue === 0 || rawValue === null || rawValue === undefined) return null;

	// Parse as number and format
	const numValue = parseFloat(rawValue);
	if (isNaN(numValue)) return String(rawValue);

	const value = Math.round(numValue).toLocaleString();

	// Get the correct unit from conversion pair
	let unit = '';
	if (Array.isArray(unitConversionPair) && unitConversionPair.length > unitIndex) {
		unit = unitConversionPair[unitIndex];
	}

	return unit ? `${value} ${unit}` : value;
};

// Transform boolean/text values
const transformBooleanValue = (value) => {
	if (typeof value === 'boolean') {
		return value ? 'Yes' : 'No';
	}
	return value;
};

// Add units to content if available - handles arrays with unit conversion pairs
const addUnitsToContent = (content, unitConversionPair, selectedUnit) => {
	if (content === null || content === undefined || content === '') return '-';

	// Handle arrays (API returns actual arrays, not JSON strings)
	if (Array.isArray(content) && content.length > 0) {
		const formatted = formatArrayValue(content, unitConversionPair);
		return formatted || '-';
	}

	// Try to parse JSON string (fallback for string-encoded arrays or objects with value property)
	if (typeof content === 'string') {
		try {
			const parsed = JSON.parse(content);

			// Handle arrays
			if (Array.isArray(parsed) && parsed.length > 0) {
				const formatted = formatArrayValue(parsed, unitConversionPair);
				return formatted || '-';
			}

			// Handle objects with value property (e.g., {"value": 50000, "unit": "miles"})
			if (parsed && typeof parsed === 'object' && 'value' in parsed) {
				const value = parsed.value;

				// Skip if value is empty, 0, null, or undefined
				if (value === '' || value === 0 || value === null || value === undefined) return '—';

				// Parse as number and format if numeric
				const numValue = parseFloat(value);
				const formattedValue = !isNaN(numValue) ? Math.round(numValue).toLocaleString() : String(value);

				// Use unit from parsed object, or fall back to selectedUnit/unitConversionPair
				const unit = parsed.unit || selectedUnit ||
					(Array.isArray(unitConversionPair) && unitConversionPair.length > 0
						? unitConversionPair[findImperialIndex(unitConversionPair)]
						: '');

				return unit ? `${formattedValue} ${unit}` : formattedValue;
			}
		} catch (e) {
			// Not JSON, continue with string handling
		}
	}

	const transformedContent = transformBooleanValue(content);

	// For non-array content, use selectedUnit or first unit from pair
	if (selectedUnit) {
		return `${transformedContent} ${selectedUnit}`;
	}
	if (Array.isArray(unitConversionPair) && unitConversionPair.length > 0) {
		const unitIndex = findImperialIndex(unitConversionPair);
		return `${transformedContent} ${unitConversionPair[unitIndex]}`;
	}

	return transformedContent;
};

// Calculate duration between two timestamps
const calculateDuration = (startTime, endTime) => {
	if (!startTime || !endTime) return '-';

	const start = new Date(startTime);
	const end = new Date(endTime);
	const diffMs = end - start;

	if (diffMs < 0) return '-';

	const hours = Math.floor(diffMs / (1000 * 60 * 60));
	const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
	const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

	return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

// Extract static info from inspection (GraphQL version)
const extractStaticInfo = (inspection, inspectionData) => {
	if (!inspection) return null;

	// GraphQL returns startTime directly
	const startTime = inspection.startTime;
	// inspectionTimeInfo is an object with start/end properties, not an array
	const endTime = inspection?.inspectionTimeInfo?.end?.timestamp;

	// Get inspector name from inspectionData (stored in data JSON field)
	let inspectorName = '-';
	if (inspectionData?.driverFirstName || inspectionData?.driverLastName) {
		inspectorName = formatInspectorName(inspectionData.driverFirstName, inspectionData.driverLastName) || '-';
	} else if (inspection.driver?.name) {
		const { firstName, lastName } = parseDriverName(inspection.driver.name);
		inspectorName = formatInspectorName(firstName, lastName) || inspection.driver.name;
	}

	// Get first asset for license/jurisdiction (no nested .asset property)
	const firstAsset = inspection?.inspectionAssets?.[0];

	const staticInfo = {
		'date': startTime ? format(new Date(startTime), 'MM/dd/yyyy') : '-',
		'time': startTime ? format(new Date(startTime), 'hh:mm:ss a') : '-',
		'inspector': inspectorName,
		'license': firstAsset?.assetLicense || '-',
		'jurisdiction': firstAsset?.assetJurisdiction || '-',
		'location': inspectionData?.geolocation || '-',
		'duration': calculateDuration(startTime, endTime),
	};

	// Add inspection detail data fields from inspectionDetails (top-level property)
	if (inspection?.inspectionDetails?.inspectionDetailData) {
		inspection.inspectionDetails.inspectionDetailData.forEach(detail => {
			const label = getTranslatedLabel(detail.inspectionDetailDataName) || 'Unknown Field';

			if (detail.inspectionDetailDataType === 'photo') {
				// Display inspection-level photos from detailMedia
				const inspectionPhotos = transformInspectionDetailMedia(inspection.inspectionDetails);
				staticInfo[label] = inspectionPhotos.length > 0 ? `${inspectionPhotos.length} photo${inspectionPhotos.length !== 1 ? 's' : ''}` : '—';
			} else {
				if (detail.inspectionDetailContent === '' || detail.inspectionDetailContent === null || detail.inspectionDetailContent === undefined) {
					staticInfo[label] = '—';
				} else {
					let content = detail.inspectionDetailContent;
					if (typeof content === 'boolean') {
						content = content ? 'Yes' : 'No';
					} else if (content === 'true' || content === 'false') {
						content = content === 'true' ? 'Yes' : 'No';
					} else {
						content = addUnitsToContent(
							content,
							detail.inspectionDetailUnitConversionPair,
							detail.inspectionDetailSelectedUnit
						);
					}
					staticInfo[label] = content;
				}
			}
		});
	}

	// Add 3rd Signature row when reviews data is present
	if (inspection?.reviews?.length > 0) {
		const review = inspection.reviews[0];
		const reviewData = review?.data;

		if (reviewData) {
			const driverName = [reviewData.driverFirstName, reviewData.driverLastName]
				.filter(Boolean)
				.join(' ')
				.trim();

			const formattedDate = review.createdOn
				? format(new Date(review.createdOn), 'MM/dd/yyyy hh:mm:ss a')
				: '';

			let signatureText = `${driverName} has reviewed this EVIR® and acknowledges the certification that all required repairs have been performed on ${formattedDate}.`;

			if (reviewData.comment) {
				signatureText += ` Driver comments: ${reviewData.comment}`;
			}

			staticInfo['3rd Signature'] = signatureText;
		}
	}

	return staticInfo;
};

// Build additional asset information
const buildAddtlAssetInfo = (inspectionAsset) => {
	if (!inspectionAsset) return [];

	const addtlAssetInfo = [];

	addtlAssetInfo.push({
		languageLabel: 'DOT',
		addtlAssetContent: inspectionAsset.assetCarrierDot || '—',
	});

	if (inspectionAsset.assetIsRental && inspectionAsset.assetRentalDot) {
		addtlAssetInfo.push({
			languageLabel: 'DOT (RENTAL)',
			addtlAssetContent: inspectionAsset.assetRentalDot,
		});
	}

	addtlAssetInfo.push({
		languageLabel: 'VIN',
		addtlAssetContent: inspectionAsset.assetVin || '—',
	});

	addtlAssetInfo.push({
		languageLabel: 'POWER UNIT',
		addtlAssetContent: inspectionAsset.assetPowerUnitNumber || '—',
	});

	addtlAssetInfo.push({
		languageLabel: 'RENTAL',
		addtlAssetContent: inspectionAsset.assetIsRental ? 'Yes' : 'No',
	});

	addtlAssetInfo.push({
		languageLabel: 'ASSET LOCATION',
		addtlAssetContent: inspectionAsset.assetDivision?.divisionName || '—',
	});

	return addtlAssetInfo;
};

// Transform defect media to usable format for display
const transformDefectMedia = (defectMedia) => {
	if (!defectMedia || !Array.isArray(defectMedia)) return [];

	return defectMedia
		.map((media) => {
			// Check if thumbnail is a URL (starts with http:// or https://)
			const isUrl = media.thumbnail && (
				media.thumbnail.startsWith('http://') ||
				media.thumbnail.startsWith('https://')
			);

			// Check if thumbnail is base64 data (doesn't start with http)
			const isBase64 = media.thumbnail && !isUrl;

			if (isUrl) {
				// Thumbnail is already a URL - use it directly
				return {
					mediaId: media.mediaId,
					mediaType: media.mediaType,
					fileKey: media.fileKey,
					thumbnail: media.thumbnail,
					url: media.thumbnail,
				};
			}

			if (isBase64) {
				// Thumbnail is base64 data - create data URL
				return {
					mediaId: media.mediaId,
					mediaType: media.mediaType,
					fileKey: media.fileKey,
					thumbnail: media.thumbnail,
					url: `data:image/${media.mediaType || 'jpeg'};base64,${media.thumbnail}`,
				};
			}

			// No thumbnail available - keep media so UI can fetch presigned URL on demand
			return {
				mediaId: media.mediaId,
				mediaType: media.mediaType,
				fileKey: media.fileKey,
				thumbnail: null,
				url: null,
			};
		})
		.filter((media) => Boolean(media?.fileKey));
};

// Transform inspection detail media (inspection-level photos) to usable format for display
const transformInspectionDetailMedia = (inspectionDetails) => {
	if (!inspectionDetails?.inspectionDetailData) return [];

	const allPhotos = [];

	inspectionDetails.inspectionDetailData.forEach((detail) => {
		if (detail.inspectionDetailDataType === 'photo' && detail.detailMedia) {
			detail.detailMedia.forEach((media) => {
				// Check if thumbnail is a URL
				const isUrl = media.thumbnail && (
					media.thumbnail.startsWith('http://') ||
					media.thumbnail.startsWith('https://')
				);

				// Check if thumbnail is base64 data
				const isBase64 = media.thumbnail && !isUrl;

				if (isUrl) {
					allPhotos.push({
						mediaId: media.mediaId,
						fileKey: media.fileKey,
						thumbnail: media.thumbnail,
						url: media.thumbnail,
						caption: detail.inspectionDetailDataName,
					});
				} else if (isBase64) {
					// Thumbnail is base64 data - create data URL
					allPhotos.push({
						mediaId: media.mediaId,
						fileKey: media.fileKey,
						thumbnail: media.thumbnail,
						url: `data:image/jpeg;base64,${media.thumbnail}`,
						caption: detail.inspectionDetailDataName,
					});
				}
			});
		}
	});

	return allPhotos;
};

// EVIRPX-637: Extract defect comments from raw inspection data JSON
// The GraphQL schema uses 'comment' but the stored data uses 'defectComment'
const extractDefectComments = (inspectionData) => {
	const commentsMap = {};
	if (!inspectionData?.inspectionAssets) return commentsMap;

	for (const asset of inspectionData.inspectionAssets) {
		for (const zone of asset.inspectionZones || []) {
			for (const component of zone.inspectionComponents || []) {
				for (const defect of component.inspectionDefects || []) {
					const defectId = defect.id || defect.defectId;
					if (defectId && defect.defectComment) {
						commentsMap[defectId] = defect.defectComment;
					}
				}
			}
		}
	}
	return commentsMap;
};

// Parse inspection-level repairs array into a map keyed by defectId
// Each repair's `data` is a JSON string containing defectId, status, mechanic info, etc.
const parseRepairs = (repairs) => {
	const repairsByDefectId = {};
	if (!repairs || !Array.isArray(repairs)) return repairsByDefectId;

	repairs.forEach((repair) => {
		if (!repair?.data) return;

		let parsed;
		try {
			parsed = typeof repair.data === 'string' ? JSON.parse(repair.data) : repair.data;
		} catch (e) {
			console.error('[parseRepairs] Failed to parse repair data:', e);
			return;
		}

		const defectId = parsed?.defectId;
		if (!defectId) return;

		const mechanicName = [parsed.mechanicFirstName, parsed.mechanicLastName]
			.filter(Boolean)
			.join(' ') || null;

		const parsedRepair = {
			defectId,
			status: parsed.status || null,
			mechanicFirstName: parsed.mechanicFirstName || null,
			mechanicLastName: parsed.mechanicLastName || null,
			mechanicName,
			comment: parsed.comment || null,
			workOrder: parsed.workOrder || null,
			resolution: parsed.resolution || null,
			createdOn: repair.createdOn || null,
			severity: parsed.severity || null,
		};

		if (!repairsByDefectId[defectId]) {
			repairsByDefectId[defectId] = [];
		}
		repairsByDefectId[defectId].push(parsedRepair);
	});

	// Sort each defect's repairs by createdOn descending
	Object.keys(repairsByDefectId).forEach((id) => {
		repairsByDefectId[id].sort((a, b) => {
			if (!a.createdOn) return 1;
			if (!b.createdOn) return -1;
			return new Date(b.createdOn) - new Date(a.createdOn);
		});
	});

	return repairsByDefectId;
};

// Transform asset data for display
const transformAssetData = (inspectionAssets, defectCommentsMap = {}, repairsByDefectId = {}) => {
	if (!inspectionAssets || inspectionAssets.length === 0) return [];

	return inspectionAssets.map(inspectionAsset => {
		const zones = inspectionAsset.inspectionZones || [];

		const hasDefects = zones.some(zone =>
			zone.inspectionComponents?.some(component =>
				component.inspectionDefects && component.inspectionDefects.length > 0
			)
		);

		return {
			assetId: inspectionAsset.assetId,
			assetName: inspectionAsset.assetName || '-',
			assetCategory: inspectionAsset.assetCategory,
			languageLabel: getTranslatedLabel(inspectionAsset.zoneLayoutName) || inspectionAsset.assetName,
			zoneLayoutName: getTranslatedLabel(inspectionAsset.zoneLayoutName),
			hasDefects,
			addtlAssetInfo: buildAddtlAssetInfo(inspectionAsset),
			inspectionZones: zones.map(zone => ({
				zoneId: zone.zoneId,
				zoneName: getTranslatedLabel(zone.zoneName) || '-',
				incomplete: ['unverified', 'uninspected'].includes(zone.verificationType),
				telemetry: zone.telemetry ? {
					timestamp: zone.telemetry.timestamp || null,
					odometer: zone.telemetry.odometer || null,
				} : null,
				inspectionComponents: (zone.inspectionComponents || []).map(component => ({
					componentId: component.componentIndex,
					componentName: getTranslatedLabel(component.componentName) || '-',
					inspectionDefects: (component.inspectionDefects || []).map(defect => {
						// Match parsed inspection-level repairs to this defect by defectId
						const matchedRepairs = repairsByDefectId[defect.id] || [];

						return {
							defectId: defect.id,
							severity: defect.severity,
							severityInfo: getSeverityInfo(defect.severity),
							conditionName: getTranslatedLabel(defect.condition) || '-',
							// EVIRPX-637: Use comment from GraphQL response, or fall back to defectComment from raw data
							comment: defect.comment || defectCommentsMap[defect.id] || null,
							status: defect.status || 'open',
							defectMedia: transformDefectMedia(defect.defectMedia),
							repairs: matchedRepairs,
						};
					}),
				})),
			})),
		};
	});
};

// Fetch inspection by ID using GraphQL
export const getInspectionDetails = createAsyncThunk(
	'inspectionDetailsRedux/getInspectionDetails',
	async ({ inspectionId, companyId }, { rejectWithValue }) => {
		try {
			const response = await fetchInspectionDetails(inspectionId, companyId);
			return response;
		} catch (error) {
			const message = error.response?.data?.message || error.message || 'Failed to fetch inspection details';
			console.error('[getInspectionDetails] Error:', error);
			return rejectWithValue(message);
		}
	}
);

export const getDefectPhotos = createAsyncThunk(
	'inspectionDetailsRedux/getDefectPhotos',
	async ({ fileKeys }, { rejectWithValue }) => {
		try {
			if (!fileKeys) {
				return {};
			}

			const response = await fetchDefectPhotos(fileKeys);
			return response;
		} catch (error) {
			const message = error.response?.data?.message || error.message || 'Failed to fetch defect photos';
			console.error('[getDefectPhotos] Error:', error);
			return rejectWithValue(message);
		}
	}
);

const inspectionDetailsPxSlice = createSlice({
	name: 'inspectionDetailsRedux',
	initialState,
	reducers: {
		clearInspectionDetails: () => initialState,
	},
	extraReducers: (builder) => {
		builder
			.addCase(getInspectionDetails.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getInspectionDetails.fulfilled, (state, action) => {
				state.loading = false;
				const inspection = action.payload;

				// Parse the data field from JSON string (GraphQL returns AWSJSON)
				let inspectionData = {};
				if (inspection?.data) {
					try {
						inspectionData = typeof inspection.data === 'string'
							? JSON.parse(inspection.data)
							: inspection.data;
					} catch (e) {
						console.error('[getInspectionDetails] Failed to parse inspection data:', e);
					}
				}

				// Store both the raw inspection and parsed data
				state.inspection = {
					...inspection,
					// Merge parsed data for compatibility with existing components
					...inspectionData,
				};
				state.staticInfo = extractStaticInfo(inspection, inspectionData);
				// EVIRPX-637: Extract defect comments from raw data to work around GraphQL field name mismatch
				const defectCommentsMap = extractDefectComments(inspectionData);
				// Parse inspection-level repairs and build a map keyed by defectId
				const repairsByDefectId = parseRepairs(inspection.repairs);
				state.assetData = transformAssetData(inspection.inspectionAssets, defectCommentsMap, repairsByDefectId);
				state.inspectionPhotos = transformInspectionDetailMedia(inspection.inspectionDetails);
				state.error = null;
			})
			.addCase(getInspectionDetails.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			})
			// Global reset
			.addCase(resetALL, () => initialState);
	},
});

export const { clearInspectionDetails } = inspectionDetailsPxSlice.actions;

export default inspectionDetailsPxSlice.reducer;
